import { Link } from 'react-router-dom';
import { useIsAuthenticated } from '@/hooks';

// Bank + circular arrow-badge icon, ported straight from the supplied
// reference markup (deposit's badge/arrow are green, withdraw's are red).
const BankArrowIcon = ({
  direction,
  className,
}: {
  direction: 'down' | 'up';
  className?: string;
}) => {
  const badgeStroke = direction === 'down' ? '#0b6a20' : '#9a000a';
  const arrowStroke = direction === 'down' ? '#0e7a24' : '#d0000f';

  return (
    <svg className={`dwtx-icon ${className ?? ''}`} viewBox="0 0 64 56" aria-hidden="true">
      <g fill="#fff">
        <polygon points="24,2 2,14 46,14" />
        <rect x="3" y="15" width="42" height="4" rx="1" />
        <rect x="7" y="21" width="6" height="20" rx="1" />
        <rect x="18" y="21" width="6" height="20" rx="1" />
        <rect x="29" y="21" width="6" height="20" rx="1" />
        <rect x="4" y="43" width="36" height="4" rx="1" />
        <rect x="0" y="49" width="40" height="4" rx="1" />
      </g>
      <circle cx="47" cy="40" r="14" fill="#fff" stroke={badgeStroke} strokeWidth={2} />
      <path
        d={direction === 'down' ? 'M47 31 v14 M41 39 l6 7 l6 -7' : 'M47 49 v-14 M41 41 l6 -7 l6 7'}
        stroke={arrowStroke}
        strokeWidth={4}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Ported as close to verbatim as possible from the supplied HTML/CSS
// reference — same colours, glow, glossy top-shine, divider and radius,
// including its own 480px breakpoint (this app's own width cap already
// matches that, but kept so the buttons stay correct if that ever changes).
const DepositWithdrawBar = () => {
  const isAuthenticated = useIsAuthenticated();

  if (!isAuthenticated) return null;

  return (
    <div className="bg-gradient-to-b from-[#20242a] to-[#111417] px-3 py-5 dark:bg-darkPanelBg">
      <style>{`
        .dwtx-row { display: flex; gap: 16px; }
        /* This app's own layout never exceeds 480px wide, and two of these
           sit side by side in it, so the fluid (clamp-based) sizing below
           is the one that's actually always in effect — it scales with the
           viewport instead of guessing a fixed size per breakpoint, which
           is what clipped "Deposit"/"Withdraw" down to "Dep"/"Wit" twice in
           a row already. The reference's original large size only kicks in
           past 480px, where each button again has a whole row to itself. */
        .dwtx-btn {
          position: relative;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: clamp(6px, 2vw, 16px);
          flex: 1;
          min-width: 0;
          height: clamp(42px, 11vw, 56px);
          padding: 0 clamp(8px, 3vw, 27px);
          border-radius: clamp(14px, 6vw, 18px);
          border: 2px solid;
          color: #fff;
          font-family: 'Montserrat','Segoe UI',Arial,sans-serif;
          font-size: clamp(13px, 4vw, 26px);
          font-weight: 800;
          letter-spacing: 0.3px;
          text-shadow: 0 2px 3px rgba(0,0,0,0.35);
          overflow: hidden;
          white-space: nowrap;
          transition: transform 0.12s ease, filter 0.2s ease;
        }
        .dwtx-btn::before {
          content: "";
          position: absolute;
          left: 4px; right: 4px; top: 3px;
          height: 45%;
          border-radius: 20px 20px 50% 50% / 20px 20px 14px 14px;
          background: linear-gradient(to bottom, rgba(255,255,255,0.28), rgba(255,255,255,0));
          pointer-events: none;
        }
        .dwtx-btn:hover { filter: brightness(1.12); }
        .dwtx-btn:active { transform: scale(0.97); }
        .dwtx-btn:focus-visible { outline: 3px solid #fff; outline-offset: 4px; }
        .dwtx-btn.deposit {
          background: linear-gradient(135deg,#22c83a 0%,#11862a 45%,#0a5c1c 100%);
          border-color: #2dff4a;
          box-shadow: 0 0 14px rgba(45,255,74,0.55), inset 0 0 12px rgba(0,0,0,0.35);
        }
        .dwtx-btn.withdraw {
          background: linear-gradient(135deg,#f0141e 0%,#c00010 45%,#8a0008 100%);
          border-color: #ff3b3b;
          box-shadow: 0 0 14px rgba(255,59,59,0.55), inset 0 0 12px rgba(0,0,0,0.35);
        }
        .dwtx-icon {
          width: clamp(18px, 6vw, 47px);
          height: clamp(16px, 5.3vw, 41px);
          flex-shrink: 0;
          filter: drop-shadow(0 2px 2px rgba(0,0,0,0.35));
        }
        .dwtx-divider {
          width: 2px; flex-shrink: 0;
          height: clamp(20px, 6vw, 37px);
          background: linear-gradient(to bottom, transparent, rgba(255,255,255,0.75), transparent);
        }
        .dwtx-label { position: relative; }
        /* Past 480px each button has a full row's width again (see the
           note on .dwtx-btn above) — capped, shorter than the original
           reference size per request ("height thoda kam karo"). */
        @media (min-width: 481px) {
          .dwtx-btn { gap: 16px; height: 56px; padding: 0 27px; border-radius: 18px; border-width: 2px; font-size: 26px; }
          .dwtx-icon { width: 47px; height: 41px; }
          .dwtx-divider { height: 37px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .dwtx-btn { transition: none; }
        }
      `}</style>

      <div className="dwtx-row">
        <Link to="/deposit" className="dwtx-btn deposit">
          <BankArrowIcon direction="down" />
          <span className="dwtx-divider" />
          <span className="dwtx-label">Deposit</span>
        </Link>

        <Link to="/withdraw" className="dwtx-btn withdraw">
          <BankArrowIcon direction="up" />
          <span className="dwtx-divider" />
          <span className="dwtx-label">Withdraw</span>
        </Link>
      </div>
    </div>
  );
};

export default DepositWithdrawBar;
